import React from "react";
import { ItemModalDraft, ItemModalErrors } from "../../utils/itemValidation";

interface ItemMetadataFormProps {
  isAdmin: boolean;
  draft: ItemModalDraft;
  errors: ItemModalErrors;
  onFieldChange: <K extends keyof ItemModalDraft>(
    key: K,
    value: ItemModalDraft[K],
  ) => void;
  readOnlyFields?: Array<keyof ItemModalDraft>;
  labels: {
    name: string;
    id: string;
    code: string;
    category: string;
    status: string;
    available: string;
    unavailable: string;
    description: string;
  };
  description?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  borderColor?: string;
  fontFamily?: string;
}

const fieldLabelStyle = (
  secondaryTextColor: string,
  fontFamily: string,
): React.CSSProperties => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: secondaryTextColor,
  fontFamily,
});

const valueTextStyle = (
  primaryTextColor: string,
  fontFamily: string,
): React.CSSProperties => ({
  fontSize: "0.875rem",
  fontWeight: 400,
  color: primaryTextColor,
  fontFamily,
});

export function ItemMetadataForm({
  isAdmin,
  draft,
  errors,
  onFieldChange,
  readOnlyFields = [],
  labels,
  description,
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  borderColor = "rgba(255, 255, 255, 0.1)",
  fontFamily = "Arial, sans-serif",
}: ItemMetadataFormProps) {
  const isReadOnly = (field: keyof ItemModalDraft) =>
    readOnlyFields.includes(field);

  const renderInput = (
    field: keyof ItemModalDraft,
    label: string,
    value: string,
    error?: string,
    type: "text" | "select" = "text",
  ) => {
    if (!isAdmin) {
      return (
        <>
          <label
            style={fieldLabelStyle(secondaryTextColor, fontFamily)}
            data-oid="94mn0at"
          >
            {label}
          </label>
          <span
            style={valueTextStyle(primaryTextColor, fontFamily)}
            data-oid=".-vvoo:"
          >
            {value || "-"}
          </span>
        </>
      );
    }

    return (
      <>
        <label
          htmlFor={`item-field-${field}`}
          style={fieldLabelStyle(secondaryTextColor, fontFamily)}
          data-oid="3zgr6cs"
        >
          {label}
        </label>
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}
          data-oid="k8t9d13"
        >
          {type === "select" ? (
            <select
              id={`item-field-${field}`}
              value={value}
              onChange={(event) =>
                onFieldChange(
                  field,
                  event.target.value as ItemModalDraft[typeof field],
                )
              }
              aria-invalid={error ? "true" : "false"}
              aria-describedby={error ? `item-field-${field}-error` : undefined}
              style={{
                borderRadius: "0.9rem",
                border: `1px solid ${borderColor}`,
                background: "rgba(255, 255, 255, 0.05)",
                color: primaryTextColor,
                fontSize: "0.875rem",
                padding: "0.7rem 0.85rem",
                fontFamily,
              }}
              data-oid="6:up52j"
            >
              <option value="available" data-oid="rf5plzw">
                {labels.available}
              </option>
              <option value="unavailable" data-oid="xgtnjey">
                {labels.unavailable}
              </option>
            </select>
          ) : (
            <input
              id={`item-field-${field}`}
              value={value}
              readOnly={isReadOnly(field)}
              onChange={(event) =>
                onFieldChange(
                  field,
                  event.target.value as ItemModalDraft[typeof field],
                )
              }
              aria-invalid={error ? "true" : "false"}
              aria-describedby={error ? `item-field-${field}-error` : undefined}
              style={{
                borderRadius: "0.9rem",
                border: `1px solid ${borderColor}`,
                background: isReadOnly(field)
                  ? "rgba(255, 255, 255, 0.03)"
                  : "rgba(255, 255, 255, 0.05)",
                color: primaryTextColor,
                fontSize: "0.875rem",
                padding: "0.7rem 0.85rem",
                fontFamily,
              }}
              data-oid="pwks-cn"
            />
          )}
          {error ? (
            <p
              id={`item-field-${field}-error`}
              role="alert"
              style={{
                margin: 0,
                fontSize: "0.75rem",
                color: "#f87171",
                fontFamily,
              }}
              data-oid="ud64o63"
            >
              {error}
            </p>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "100px 1fr",
        gap: "0.75rem",
        rowGap: "1rem",
      }}
      data-oid="o5gdysn"
    >
      {renderInput("name", labels.name, draft.name, errors.name)}
      {renderInput("id", labels.id, draft.id)}
      {renderInput("code", labels.code, draft.code, errors.code)}
      {renderInput(
        "category",
        labels.category,
        draft.category,
        errors.category,
      )}
      {renderInput("status", labels.status, draft.status, undefined, "select")}

      {description ? (
        <>
          <label
            style={fieldLabelStyle(secondaryTextColor, fontFamily)}
            data-oid="k50d4m6"
          >
            {labels.description}
          </label>
          <span
            style={{
              ...valueTextStyle(primaryTextColor, fontFamily),
              lineHeight: 1.6,
              wordWrap: "break-word",
            }}
            data-oid="pahjl:e"
          >
            {description}
          </span>
        </>
      ) : null}
    </div>
  );
}
